import { View, StyleSheet, ScrollView } from "react-native";
import { MotiView } from "moti";
import SkipLink from "../components/common/general/SkipLink";
import { useAppState } from "../contexts/AppStateContext";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import MainMenuContainer from "../containers/navigation/MainMenuContainer/MainMenuContainer";
import ChatBarContainer from "../containers/navigation/ChatBarContainer/ChatBarContainer";
import ContentMenuContainer from "../containers/navigation/ContentMenuContainer/ContentMenuContainer";
import ChatWindowContainer from "../containers/chatWindow/ChatWindowContainer/ChatWindowContainer";
import { useChatBar } from "../contexts/ChatBarContext";
import CustomCourseworkMenuContainer from "../containers/navigation/CustomCourseworkMenuContainer.js/CustomCourseworkMenuContainer";
import { useAppConfig } from "../AppConfigProvider";
import TopMenuContainer from "../containers/navigation/TopMenuContainer/TopMenuContainer";
import MobileDefaultLayout from "./MobileDefaultLayout";
import CourseHeaderContainer from "../containers/customCoursework/CourseHeaderContainer";
import ComingSoonOverlay from "../components/navigation/ComingSoonOverlay/ComingSoonOverlay";
import PreviewModeBannerContainer from "../containers/adminDashboard/PreviewModeBannerContainer/PreviewModeBannerContainer";
import { usePreviewMode } from "../contexts/PreviewModeContext";
import { useRoute } from "@react-navigation/native";

function DefaultLayout({
  children,
  menuType = "default",
  pageHasChat = true,
  courseName = "",
  showCourseNav = false,
  withPadding = true,
  comingSoon = false,
}) {
  const { state } = useAppState();
  const appConfig = useAppConfig();
  const isAxioOffering = appConfig.isAxioOffering;
  const { isMedium } = useResponsiveScreen();
  const { showChat } = useChatBar();
  const { isPreviewMode } = usePreviewMode();

  const route = useRoute();
  const EDITOR_ROUTES = ["Custom Coursework Modules"];

  const isEditor = EDITOR_ROUTES.includes(route.name);

  // Helper to select sidebar component
  const sidebar = (() => {
    switch (menuType) {
      case "customCoursework":
        return <CustomCourseworkMenuContainer />;
      case "content":
        return <ContentMenuContainer courseName={courseName} />;
      case "none":
        return null;
      default:
        if (!isAxioOffering) {
          return <MainMenuContainer />;
        }
        return null;
    }
  })();

  if (!isMedium) {
    return (
      <MobileDefaultLayout
        menuType={menuType}
        courseName={courseName}
        pageHasChat={pageHasChat}
        showCourseNav={showCourseNav}>
        {isPreviewMode && <PreviewModeBannerContainer />}
        {children}
      </MobileDefaultLayout>
    );
  }

  return (
    <View style={styles.root}>
      <SkipLink />
      {isPreviewMode && <PreviewModeBannerContainer />}

      {isAxioOffering && (
        <View id="header-container" style={styles.headerContainer} role="banner" accessibilityRole="header">
          {showCourseNav ? <CourseHeaderContainer isEditor={isEditor} /> : <TopMenuContainer />}
        </View>
      )}
      <View style={styles.mainRow}>
        {sidebar && (
          <View
            role="navigation"
            aria-label="Sidebar navigation"
            accessibilityRole="navigation"
            accessibilityLabel="Sidebar navigation">
            {sidebar}
          </View>
        )}
        {/* Main content area */}
        <ScrollView
          style={styles.contentArea}
          contentContainerStyle={styles.contentContainer}
          id="main-content"
          tabIndex={0}
          role="main">
          <View style={withPadding ? styles.centerWrapperPadding : styles.centerWrapper}>
            <View style={styles.childrenContainer}>{children}</View>
          </View>
          {pageHasChat && (
            <MotiView
              style={styles.chatBarContainer}
              animate={{
                scale: state.meta.isExpanded ? 1 : 1,
                opacity: state.meta.isExpanded ? 1 : 1,
              }}
              transition={{ type: "timing", duration: 650 }}>
              <ChatBarContainer />
            </MotiView>
          )}
        </ScrollView>
        {comingSoon && <ComingSoonOverlay />}
      </View>
      {showChat && <ChatWindowContainer />}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    // Web: fill the viewport so the ScrollView can scroll within it
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flex: 1,
    flexDirection: "column",
    backgroundColor: "#fff",
    minHeight: 0,
    overflow: "hidden",
  },
  mainRow: {
    flexDirection: "row",
    marginTop: 30,
    flex: 1,
    // Web flexbox quirk: allow children (ScrollView) to shrink and scroll
    minHeight: 0,
    overflow: "hidden",
  },
  headerContainer: {
    width: "100%",
    zIndex: 10,
    backgroundColor: "#fff",
  },
  contentArea: {
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
    position: "relative",
    backgroundColor: "#fff",
    overflow: "scroll",
  },
  contentContainer: {
    // Keep normal layout behavior while allowing scrolling when content grows
    flexGrow: 1,
    minHeight: 0,
  },
  centerWrapperPadding: {
    flex: 1,
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 0,
  },
  centerWrapper: { flex: 1 },
  childrenContainer: {
    maxWidth: 1340,
    width: "100%",
    marginHorizontal: "auto",
    marginBottom: 100,
  },
  chatBarContainer: {
    width: "100%",
    alignSelf: "center",
    zIndex: 999,
    paddingHorizontal: 40,
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
});

export default DefaultLayout;
